// Localized storefront display.
import { tr, useStoreLanguage } from '@/i18n';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from '@/i18n/router';
import { useCart } from '@/hooks/useCart';
import { useWishlist } from '@/hooks/useWishlist';
import { useAuth } from '@/hooks/useAuth';
import SearchAutocomplete from './SearchAutocomplete';
import BlogNotificationsPanel from './BlogNotificationsPanel';
import LanguageSelector from './LanguageSelector';

const topBarMessages = [
  { icon: 'ri-truck-line', text: 'Envío gratis desde 35€' },
  { icon: 'ri-coupon-3-line', text: '-15% primera compra: BIENVENIDA15' },
  { icon: 'ri-shield-check-line', text: 'Pago seguro · Devoluciones fáciles' },
];

const TopBarStrip = ({ transparent }: { transparent: boolean }) => {
  useStoreLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % topBarMessages.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);
    return () => {
      clearInterval(interval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const current = topBarMessages[currentIndex];

  return (
    <div className={`w-full min-h-8 flex items-center justify-center overflow-hidden transition-colors duration-300 ${
      transparent ? 'bg-black/20' : 'bg-forest-800'
    }`}>
      <div
        className={`flex items-center gap-2 transition-all duration-300 ${
          isAnimating ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'
        }`}
      >
        <i className={`${current.icon} text-amber-400 text-xs w-4 h-4 flex items-center justify-center`} aria-hidden="true"></i>
        <span className="font-inter text-[10px] sm:text-xs leading-tight text-center px-2 text-white">{tr(current.text)}</span>
      </div>
    </div>
  );
};

const Navbar = () => {
  useStoreLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const { cartCount, setIsCartOpen } = useCart();
  const { totalWishlistItems } = useWishlist();
  const { user, profile, signOut } = useAuth();

  // Determinar si estamos en la página de inicio
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Atajo de teclado Ctrl+K / Cmd+K para abrir búsqueda
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cerrar menú de usuario al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    await signOut();
    setIsUserMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/products', label: 'Productos' },
    { path: '/bundles', label: 'Packs' },
    { path: '/offers', label: 'Ofertas' },
    { path: '/recipes', label: 'Recetas' },
    { path: '/about-us', label: 'Nosotros' },
    { path: '/contact', label: 'Contacto' },
  ];

  // Si no estamos en la página de inicio, siempre mostrar fondo blanco
  const shouldShowWhiteBg = !isHomePage || isScrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        shouldShowWhiteBg
          ? 'bg-white/90 backdrop-blur-md border-b border-sand-200'
          : 'bg-transparent'
      }`}
    >
      {/* TopBar strip — integrado dentro del nav para no solaparse */}
      <TopBarStrip transparent={!shouldShowWhiteBg} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* h-16 md:h-18 — altura fija, overflow-hidden para que el logo nunca sobresalga */}
        <div className="flex justify-between items-center h-16 md:h-[72px]">
          {/* Logo — py-2 garantiza margen vertical dentro de la barra */}
          <Link 
            to="/" 
            className="flex items-center py-2 transition-all duration-300 hover:opacity-90"
          >
            <img 
              src="/images/solsazon-logo.png"
              alt={tr("Solsazón")}
              className="h-full max-h-14 max-w-[80px] sm:max-w-none w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-4">
            {tr(navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-inter text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  location.pathname === link.path
                    ? shouldShowWhiteBg
                      ? 'text-solsazon-green'
                      : 'text-white font-semibold'
                    : shouldShowWhiteBg
                    ? 'text-solsazon-copper hover:text-solsazon-green'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {tr(link.label)}
              </Link>
            )))}
          </div>

          {/* Cart & Wishlist & User Icons */}
          <div className="hidden xl:flex items-center space-x-3">
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`w-10 h-10 flex items-center justify-center cursor-pointer transition-colors group relative ${
                shouldShowWhiteBg ? 'text-solsazon-copper hover:text-solsazon-green' : 'text-white hover:text-white/80'
              }`}
            >
              <i className="ri-search-line text-xl"></i>
              <span className={`absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-inter px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
                shouldShowWhiteBg ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
              }`}>{tr("Ctrl+K")}</span>
            </button>
            
            {/* User Menu */}
            <div className="relative user-menu-container">
              <button 
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${
                  shouldShowWhiteBg ? 'text-solsazon-copper hover:text-solsazon-green' : 'text-white hover:text-white/80'
                }`}
              >
                <i className="ri-user-line text-xl"></i>
              </button>
              
              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg border border-sand-200 py-2 z-50">
                  {user ? (
                    <>
                      <div className="px-4 py-3 border-b border-sand-100">
                        <p className="text-sm font-medium text-solsazon-dark truncate">
                          {profile?.first_name || tr('Usuario')}
                        </p>
                        <p className="text-xs text-solsazon-copper truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/account"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-solsazon-dark hover:bg-sand-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-line"></i>{tr("Mi Cuenta")}</Link>
                      <Link
                        to="/mis-puntos"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-solsazon-dark hover:bg-sand-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-star-line text-amber-500"></i>{tr("Mis Puntos")}</Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-logout-box-line"></i>{tr("Cerrar Sesión")}</button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-solsazon-dark hover:bg-sand-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-login-box-line"></i>{tr("Iniciar Sesión")}</Link>
                      <Link
                        to="/register"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-solsazon-dark hover:bg-sand-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-add-line"></i>{tr("Registrarse")}</Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Blog Notifications — solo si hay usuario */}
            {tr(user && (
              <div className={shouldShowWhiteBg ? '' : 'text-white'}>
                <BlogNotificationsPanel />
              </div>
            ))}

            <Link
              to="/wishlist"
              className={`w-10 h-10 flex items-center justify-center cursor-pointer transition-colors relative ${
                shouldShowWhiteBg ? 'text-solsazon-copper hover:text-rose-500' : 'text-white hover:text-white/80'
              }`}
            >
              <i className="ri-heart-3-line text-xl"></i>
              {tr(totalWishlistItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-inter font-semibold">
                  {tr(totalWishlistItems)}
                </span>
              ))}
            </Link>
            <button 
              onClick={() => setIsCartOpen(true)}
              className={`w-10 h-10 flex items-center justify-center cursor-pointer transition-colors relative ${
                shouldShowWhiteBg ? 'text-solsazon-copper hover:text-solsazon-green' : 'text-white hover:text-white/80'
              }`}
            >
              <i className="ri-shopping-cart-line text-xl"></i>
              {tr(cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-solsazon-green text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-inter font-semibold">
                  {tr(cartCount)}
                </span>
              ))}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex xl:hidden items-center gap-0.5 ml-auto mr-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className={`w-8 h-10 flex items-center justify-center cursor-pointer ${
                shouldShowWhiteBg ? 'text-solsazon-copper' : 'text-white'
              }`}
            >
              <i className="ri-search-line text-xl"></i>
            </button>
            <Link
              to="/wishlist"
              className={`hidden min-[400px]:flex w-8 h-10 items-center justify-center cursor-pointer relative ${
                shouldShowWhiteBg ? 'text-solsazon-copper' : 'text-white'
              }`}
            >
              <i className="ri-heart-3-line text-xl"></i>
              {tr(totalWishlistItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-inter font-semibold">
                  {tr(totalWishlistItems)}
                </span>
              ))}
            </Link>
            <button 
              onClick={() => setIsCartOpen(true)}
              className={`w-8 h-10 flex items-center justify-center cursor-pointer relative ${
                shouldShowWhiteBg ? 'text-solsazon-copper' : 'text-white'
              }`}
            >
              <i className="ri-shopping-cart-line text-xl"></i>
              {tr(cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-solsazon-green text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-inter font-semibold">
                  {tr(cartCount)}
                </span>
              ))}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-8 h-10 flex items-center justify-center cursor-pointer ${
                shouldShowWhiteBg ? 'text-solsazon-copper' : 'text-white'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          <LanguageSelector light={!shouldShowWhiteBg} />
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="xl:hidden bg-solsazon-beige border-t border-sand-200">
          <div className="px-4 py-4 space-y-3">
            {tr(navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-2 font-inter text-sm font-medium cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-solsazon-green'
                    : 'text-solsazon-copper hover:text-solsazon-green'
                }`}
              >
                {tr(link.label)}
              </Link>
            )))}
            
            <Link to="/wishlist" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 font-inter text-sm font-medium text-solsazon-copper">{tr("Favoritos ")}{tr(totalWishlistItems > 0 ? `(${totalWishlistItems})` : '')}
            </Link>
            {/* User Menu Mobile */}
            <div className="border-t border-sand-200 pt-3 mt-3">
              {user ? (
                <>
                  <div className="px-2 py-2 mb-2">
                    <p className="text-sm font-medium text-solsazon-dark">
                      {profile?.first_name || tr('Usuario')}
                    </p>
                    <p className="text-xs text-solsazon-copper">{user.email}</p>
                  </div>
                  <Link
                    to="/account"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm text-solsazon-dark hover:text-solsazon-green cursor-pointer"
                  >
                    <i className="ri-user-line"></i>{tr("Mi Cuenta")}</Link>
                  <Link
                    to="/mis-puntos"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm text-solsazon-dark hover:text-solsazon-green cursor-pointer"
                  >
                    <i className="ri-star-line text-amber-500"></i>{tr("Mis Puntos")}</Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 py-2 text-sm text-red-600 hover:text-red-700 cursor-pointer"
                  >
                    <i className="ri-logout-box-line"></i>{tr("Cerrar Sesión")}</button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm text-solsazon-dark hover:text-solsazon-green cursor-pointer"
                  >
                    <i className="ri-login-box-line"></i>{tr("Iniciar Sesión")}</Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-sm text-solsazon-dark hover:text-solsazon-green cursor-pointer"
                  >
                    <i className="ri-user-add-line"></i>{tr("Registrarse")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Search Autocomplete */}
      <SearchAutocomplete
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        isDark={!shouldShowWhiteBg}
      />
    </nav>
  );
};

export default Navbar;
